import findKey from 'lodash/findKey';
import isPlainObject from 'lodash/isPlainObject';

/**
 * Recursively searches an object to find a specified value and returns the path to that value
 *
 * This function is used to locate where a specific "pill" value (template) is mapped within
 * a datasource object structure. It recursively traverses the object to find the exact location
 * of the specified value and returns the path as an array of keys.
 *
 * @param object - The object to search within (typically a datasource object)
 * @param value - The pill value/template to search for
 * @returns An array of keys representing the path to the value, or null if not found
 */
export const getPathForMappedPill = (
  object: Record<string, unknown>,
  value: string,
): string[] | null => {
  // First, check if the value exists directly at the top level of the object
  const keyMatch = findKey(object, (currentValue) => currentValue === value);

  // If found at the top level, return a single-element path array
  if (keyMatch) {
    return [keyMatch];
  }

  // If not found at the top level, recursively search through nested objects
  for (const [key, currentValue] of Object.entries(object)) {
    // Only search within plain objects (not arrays, functions, etc.)
    if (isPlainObject(currentValue)) {
      // Recursively search the nested object
      const childPath = getPathForMappedPill(currentValue as Record<string, unknown>, value);

      // If a path is found in the child object, prepend the current key and return the full path
      if (childPath) {
        return [key, ...childPath];
      }
    }
  }

  // Return null if the value is not found anywhere in the object structure
  return null;
};
